import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import {Form, FieldArray} from 'formik';
import FieldCheckbox from '../../components/FieldCheckbox';
import FieldDatepicker from '../../components/FieldDatepicker';
import FieldSelect from '../../components/FieldSelect';
import FieldInput from '../../components/FieldInput';
import Button from '../../components/Button';
import ErrorValidation from '../../components/ErrorValidation';
import TooltipItem from "../../components/TooltipItem";
import AverageHoursMessage from "../../components/AverageHoursMessage";
import Link from "../../components/Link";
import {whatsapp} from '../../config/common.json';
import styles from './styles.module.scss';


const prevJob = {
  fromDate: null,
  toDate: null,
  occupation: {value: null},
};

const PreviousJob = ({
                       fields: {
                         fromDate,
                         toDate,
                         occupation,
                       },
                       jobs
                     }) => {
  return (
    <>
      <div className={`${styles.input} ${styles.titleBold}`}>
        <FormattedMessage
          id='step8.form.workDetails.note'
          defaultMessage='Work details'
        />
      </div>
      <div className={styles.twoInRow}>
        <FieldDatepicker
          fieldId={fromDate}
          label={(
            <FormattedMessage
              id='step8.form.fromDate.label'
              defaultMessage='From date'
            />
          )}
          containerStyles={styles.input}
        />
        <FieldDatepicker
          fieldId={toDate}
          label={(
            <FormattedMessage
              id='step8.form.toDate.label'
              defaultMessage='To date'
            />
          )}
          containerStyles={styles.input}
        />
      </div>
      <FieldSelect
        fieldId={occupation}
        loadOptions={jobs}
        label={(
          <FormattedMessage
            id='step8.form.job.label'
            defaultMessage='Business'
          />
        )}
      />
    </>
  )
};

const OtherJobs = ({jobs, values}) => (
  <FieldArray name={'otherJobs'}>
    {({remove, push}) => (
      <>
        {
          values.otherJobs.length > 0 && values.otherJobs.map((prevJob, index) => (
            <div key={index} className={`${styles.contentBlock} ${styles.prevJob}`}>
              <PreviousJob
                jobs={jobs}
                fields={{
                  fromDate: `otherJobs.${index}.fromDate`,
                  toDate: `otherJobs.${index}.toDate`,
                  occupation: `otherJobs.${index}.occupation`,
                }}
              />
              {
                index > 0 && (
                  <Button
                    type='button'
                    variant='secondary'
                    className={styles.removeBtn}
                    onClick={() => remove(index)}
                    label={(
                      <FormattedMessage
                        id='step8.form.button.removeJob'
                        defaultMessage='- Remove previous job'
                      />
                    )}
                  />
                )
              }
            </div>
          ))
        }
        <div className={styles.contentBlock}>
          <Button
            type='button'
            variant='secondary'
            onClick={() => push(prevJob)}
            label={(
              <FormattedMessage
                id='step8.form.button.addJob'
                defaultMessage='+ Add another previous job'
              />
            )}
          />
        </div>
      </>
    )}
  </FieldArray>
);

const Step8View = ({
                     loadOptions: {
                       hours,
                       jobs
                     },
                     values,
                     isValid
                   }) => {

  return (
    <Form className={styles.formContainer}>
      <div className={`${styles.innerPadding} ${styles.input}`}>
        <div className={styles.titleBold}>
          <FormattedMessage
            id="step8.form.title"
          />
        </div>
      </div>
      <div className={styles.contentBlock}>
        <FieldDatepicker
          fieldId='freelanceStartDate'
          label={(
            <FormattedMessage
              id='step8.form.freelanceStartDate.label'
              defaultMessage='I started working as a freelancer starting on a date'
            />
          )}
          containerStyles={styles.input}
        />
        <FieldSelect
          fieldId='weeklyAverageWorkHours'
          loadOptions={hours}
          label={(
            <>
              <FormattedMessage
                id='step8.form.hoursPerWeek.label'
                defaultMessage='Average working hours per week'
              />
              <TooltipItem
                id={'step8.form.hoursPerWeek'}
                tooltipText={
                  <FormattedMessage
                    id='step8.form.hoursPerWeek.bubble'
                    defaultMessage='Mark how many hours a week you will devote to your work through us.
                    Please note, if you choose the 1-11 hours option,
                    you will be considered as someone who does not work and
                    will only pay the minimum to National Insurance.
                    In addition, you will not be able to receive pensions from National Insurance.'
                  />
                }
              />
            </>
          )}
          containerStyles={styles.input}
        />
        <AverageHoursMessage
          value={values.weeklyAverageWorkHours.value}
        />
        <FieldInput
          fieldId='monthlyAverageIncome'
          type='number'
          label={(
            <>
              <FormattedMessage
                id='step8.form.monthlyIncome.label'
                defaultMessage='Average monthly income in NIS'
              />
              <TooltipItem
                id={'step8.form.monthlyIncome'}
                tooltipText={
                  <FormattedMessage
                    id='step8.form.monthlyIncome.bubble'
                    defaultMessage="Enter how much you will earn through us on average per month.
                    According to this determination, you will pay the monthly advance to National Insurance.
                    Don't worry, it can be changed during the year if you earn more or less."
                  />
                }
              />
            </>
          )}
          containerStyles={styles.input}
          disabled={values.monthlyAverageIncomeIsNotSpecified}
        />
        <FieldCheckbox
          fieldId={'monthlyAverageIncomeIsNotSpecified'}
          label={(
            <FormattedMessage
              id='step8.form.monthlyAverageIncomeAbsent.label'
              defaultMessage='I do not currently know the average monthly income'
            />
          )}
          containerStyles={`${styles.input} ${values.monthlyAverageIncomeIsNotSpecified ? styles.customCheckboxMargin : ''}`}
        />
        <div className={styles.note}>
          <FormattedMessage
            id="step8.form.description.label"
          />
        </div>
      </div>
      <div className={styles.customInnerPadding}>
        <div className={`${styles.titleBold} ${styles.input}`}>
          <FormattedMessage
            id="step8.checkbox.title"
          />
          <TooltipItem
            id={'step8.checkbox'}
            tooltipText={
              <FormattedMessage
                id='step8.checkbox.title.bubble'
                defaultMessage='There is no obligation to mark this option.'
              />
            }
          />
        </div>
        <FieldCheckbox
          fieldId={'workedAsEmployee'}
          label={(
            <FormattedMessage
              id='step8.form.workedAsEmployee.label'
              defaultMessage='I have worked as an employee for the past two years'
            />
          )}
          containerStyles={styles.input}
        />
      </div>
      {values.workedAsEmployee && <OtherJobs jobs={jobs} values={values}/>}
      <Link
        label={
          <FormattedMessage
            id={'common.whatsapp.link'}
            defaultMessage={'Need help? Click to switch to WhatsApp'}
          />
        }
        link={whatsapp}
        customContainerStyles={styles.innerPadding}
      />
      <div className={styles.innerPadding}>
        <Button
          type='submit'
          label={(
            <FormattedMessage
              id='common.form.button.save'
              defaultMessage='Start'
            />
          )}
        />
        {!isValid && <ErrorValidation/>}
      </div>
    </Form>
  )
};

Step8View.propTypes = {
  loadOptions: PropTypes.shape({
    hours: PropTypes.func,
    jobs: PropTypes.func,
  }),
  values: PropTypes.object,
  isValid: PropTypes.bool,
};

export default Step8View;
